import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Info } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Small shared pieces that give every settings section the same visual
 * language: tinted sticky bars, accent icon chips, a save-state indicator and
 * on/off status text. Colour is always paired with a word or an icon.
 */

/** "Unsaved changes" / "No unsaved changes" — left side of the action bar. */
export function SaveStatus({ dirty, children }: { dirty: boolean; children?: ReactNode }) {
  return dirty ? (
    <p className="flex items-center gap-2 text-[13px] font-semibold text-amber-700" role="status">
      <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-bridge-warning" />
      {children ?? "Unsaved changes"}
    </p>
  ) : (
    <p className="flex items-center gap-1.5 text-[13px] text-bridge-muted">
      <Check size={14} aria-hidden className="shrink-0 text-bridge-success" />
      No unsaved changes
    </p>
  );
}

/** Tinted icon chip used before a group heading inside a section. */
export function IconChip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-bridge-accent/10 text-bridge-accent",
        className,
      )}
    >
      {children}
    </span>
  );
}

/** Group heading inside a section: tinted icon chip + muted uppercase label. */
export function GroupHeading({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2.5">
      <IconChip>{icon}</IconChip>
      <h4 className="text-sm font-bold uppercase tracking-wider text-bridge-muted">{children}</h4>
    </div>
  );
}

/** Blue info callout — used for "pick a scope first" guidance. */
export function InfoCallout({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-bridge-accent/20 bg-bridge-accent/5 px-3 py-2 text-xs text-bridge-fg">
      <Info size={14} aria-hidden className="mt-px shrink-0 text-bridge-accent" />
      <span>{children}</span>
    </div>
  );
}

/**
 * Tooltip body for a blocker that is resolved on another page: the message
 * plus an icon link that goes there. Only for off-page fixes — anything the
 * user can do on this page, or nothing to do but wait, stays plain text.
 */
export function TooltipWithLink({
  children,
  to,
  label,
}: {
  children: ReactNode;
  to: string;
  /** Accessible name / hover title, e.g. "Open Integrations". */
  label: string;
}) {
  return (
    <span className="flex items-start gap-3">
      <span>{children}</span>
      <Link
        to={to}
        aria-label={label}
        title={label}
        className="mt-px inline-flex shrink-0 cursor-pointer rounded-md bg-[#ffffff1a] p-1 text-zinc-50 transition-colors hover:bg-[#ffffff40] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
      >
        <ArrowUpRight size={14} aria-hidden />
      </Link>
    </span>
  );
}

/** "On" / "Off" word beside a switch, so state never rests on colour alone. */
export function OnOffLabel({ on }: { on: boolean }) {
  return (
    <span
      className={cn(
        "w-7 text-right text-[11px] font-bold uppercase tracking-wider",
        on ? "text-bridge-accent" : "text-bridge-subtle",
      )}
    >
      {on ? "On" : "Off"}
    </span>
  );
}
